// Minimal DOM helpers — no framework. `h` builds elements, `mount` renders into #app.
type Child = Node | string | null | undefined | Child[];
export function h(tag: string, attrs: Record<string, any> = {}, ...children: Child[]): HTMLElement {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className = v;
    else if (k === 'html') el.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'dataset') Object.assign(el.dataset, v);
    else if (k in el && k !== 'list' && k !== 'type') (el as any)[k] = v;
    else el.setAttribute(k, String(v));
  }
  for (const c of (children as any[]).flat(Infinity)) {
    if (c == null || c === false) continue;
    el.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

// Wrap a <table class="table"> in a horizontally-scrollable container so wide
// tables never clip unreachable columns on mobile (the global
// `html,body{overflow-x:hidden}` prevents page-level scroll). ALL tables must
// be wrapped via this helper — new table renderers should call tableScroll(...).
export function tableScroll(table: HTMLElement): HTMLElement {
  const scroller = h('div', { class: 'table-scroll' }, table);
  // Outer non-scrolling frame owns the edge-fade pseudo-elements. On iOS Safari,
  // absolutely-positioned pseudo-elements on an overflow-scroll container anchor
  // to the SCROLLED CONTENT box (scrollWidth), not the visible viewport frame —
  // which pushes the left fade into the middle of a wide table (a vertical seam).
  // Anchoring the fades to this fixed, non-scrolling frame keeps them pinned to
  // the visible edges. Geometry is still read from the inner scroller.
  const frame = h('div', { class: 'table-scroll-frame' }, scroller);
  enhanceTableScroll(scroller);
  return frame;
}

// Pure logic: given a scroll container's geometry, decide whether there is more
// content to the left / right (i.e. whether to show the fade affordances).
// `left` = scrolled away from the start; `right` = more content remains right.
// A 1px tolerance absorbs sub-pixel rounding so the right fade reliably hides
// at the true end of scroll.
export function scrollAffordanceState(
  { scrollLeft, scrollWidth, clientWidth }: { scrollLeft: number; scrollWidth: number; clientWidth: number },
): { left: boolean; right: boolean } {
  const maxScroll = scrollWidth - clientWidth;
  if (maxScroll <= 1) return { left: false, right: false };
  return { left: scrollLeft > 1, right: scrollLeft < maxScroll - 1 };
}

// Attach a horizontal-scroll affordance to a `.table-scroll` wrapper: toggles
// `.can-scroll-left` / `.can-scroll-right` classes (CSS renders edge fades) as
// the user scrolls, resizes, or the table content changes. Feature-detected and
// SSR-safe: no-ops without a real DOM / ResizeObserver.
export function enhanceTableScroll(wrapper: HTMLElement): void {
  if (typeof window === 'undefined' || typeof (wrapper as any).addEventListener !== 'function') return;
  // Geometry/scrollLeft are read from the SCROLLER (`wrapper`), but the
  // can-scroll-* classes are applied to its non-scrolling parent frame so the
  // CSS edge-fade pseudo-elements (which live on `.table-scroll-frame`) anchor
  // to the fixed viewport box on iOS Safari. Fall back to the scroller itself
  // when there is no parent frame (defensive; tableScroll always wraps one).
  const target: HTMLElement = (wrapper.parentElement as HTMLElement | null) ?? wrapper;
  const update = (): void => {
    const { left, right } = scrollAffordanceState({
      scrollLeft: wrapper.scrollLeft,
      scrollWidth: wrapper.scrollWidth,
      clientWidth: wrapper.clientWidth,
    });
    target.classList.toggle('can-scroll-left', left);
    target.classList.toggle('can-scroll-right', right);
  };
  wrapper.addEventListener('scroll', update, { passive: true } as any);
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(update);
    ro.observe(wrapper);
    const table = wrapper.firstElementChild;
    if (table) ro.observe(table);
  } else if (typeof window.addEventListener === 'function') {
    window.addEventListener('resize', update);
  }
  // Defer the initial measure until after layout so scrollWidth is populated.
  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: () => void) => setTimeout(cb, 0);
  raf(() => raf(update));
}

export function frag(...children: (Node | string | null | undefined)[]): DocumentFragment {
  const f = document.createDocumentFragment();
  for (const c of children) if (c != null) f.append(typeof c === 'string' ? document.createTextNode(c) : c);
  return f;
}

export function clear(el: HTMLElement): void { el.replaceChildren(); }

let toastTimer: number | undefined;
export function toast(msg: string, kind: 'ok' | 'warn' | 'danger' = 'ok'): void {
  let t = document.getElementById('vid-toast');
  if (!t) {
    t = h('div', { id: 'vid-toast', role: 'status', style: 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);z-index:100;padding:10px 18px;border-radius:10px;box-shadow:var(--shadow);font-weight:600;font-size:.9rem' });
    document.body.append(t);
  }
  const colors = { ok: 'var(--ok)', warn: 'var(--warn)', danger: 'var(--danger)' };
  t.style.background = colors[kind]; t.style.color = '#fff'; t.textContent = msg; t.style.opacity = '1';
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { if (t) t.style.opacity = '0'; }, 3200);
}

// Inline SVG icon (24x24, currentColor) — pass a path `d` or full inner markup.
export function icon(inner: string, size = 18): HTMLElement {
  return h('span', { class: 'icon', style: `display:inline-flex;width:${size}px;height:${size}px`, html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>` });
}

// Brand lockup logo: the new VirtualID indigo tile (public/logo.svg -> /logo.svg).
// Rendered as an <img> with alt='' + aria-hidden because the adjacent
// "VirtualID" wordmark text is the accessible name. Square asset — never distort.
export function brandLogo(size = 26): HTMLElement {
  return h('img', { src: '/logo.svg', width: String(size), height: String(size), alt: '', 'aria-hidden': 'true', class: 'brand-logo', style: 'display:block;border-radius:22%' });
}

export function avatar(name: string, size = 44): HTMLElement {
  const initials = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return h('div', { 'aria-hidden': 'true', style: `width:${size}px;height:${size}px;border-radius:50%;background:var(--brand-soft);color:var(--brand);display:flex;align-items:center;justify-content:center;font-weight:720;flex:none` }, initials);
}

export function confirmDialog(message: string): boolean {
  return window.confirm(message);
}

// --- Row / inline action controls -------------------------------------------
// The portal previously rendered the SAME kinds of row actions (Edit, Enable,
// Disable, Revoke, Remove, Delete, Rename, Copy, View, QR…) two different ways:
// as buttons in some views and as bare `<a href="#">` text links (dot-separated)
// in others. `rowAction` is the single source of truth: it always returns a real
// <button> so these controls are consistent, tappable, and accessible. Wrap a
// group in `rowActions(...)` to lay them out with consistent gap (no ` · ` dots).

export type RowActionVariant = 'default' | 'primary' | 'danger';
export interface RowActionOpts {
  variant?: RowActionVariant;
  disabled?: boolean;
  ariaLabel?: string;
  title?: string;
}

// Pure mapping: variant -> full button class string. Exposed for unit testing.
export function rowActionClass(variant: RowActionVariant = 'default'): string {
  const variantClass = variant === 'primary' ? 'btn-primary' : variant === 'danger' ? 'btn-danger' : 'btn-ghost';
  return `btn btn-sm ${variantClass}`;
}

export function rowAction(label: string, onClick: (e: Event) => void, opts: RowActionOpts = {}): HTMLButtonElement {
  const attrs: Record<string, any> = { type: 'button', class: rowActionClass(opts.variant) };
  if (opts.disabled) attrs.disabled = 'true';
  if (opts.ariaLabel) attrs['aria-label'] = opts.ariaLabel;
  if (opts.title) attrs.title = opts.title;
  const btn = h('button', attrs, label) as HTMLButtonElement;
  // Pass the handler through untouched so each call site keeps its existing
  // async try/catch + toast behavior. Disabled buttons don't fire clicks.
  if (!opts.disabled) btn.addEventListener('click', onClick);
  return btn;
}

// Consistent flex container for a group of row actions — replaces the old
// dot-separated (` · `) anchor arrays. Wraps in narrow table cells.
export function rowActions(...children: (Node | null | undefined | false)[]): HTMLElement {
  return h('div', { class: 'row-actions' }, ...children.filter((c): c is Node => !!c));
}
